import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material';
import { companiesService } from '../../services/api';
import { Company } from '../../types';

interface AddRfidCardModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (rfidData: RfidFormData) => void;
  isLoading?: boolean;
  orgId: string;
  unitId: string;
  mode?: 'create' | 'edit';
  initialData?: RfidFormData;
}

export interface RfidFormData {
  rfid: string;
  serial_number: string;
  user_name: string;
  email: string;
  company: string;
}

const emptyFormData: RfidFormData = {
  rfid: '',
  serial_number: '',
  user_name: '',
  email: '',
  company: '',
};

const AddRfidCardModal: React.FC<AddRfidCardModalProps> = ({ 
  open, 
  onClose, 
  onSubmit, 
  isLoading = false,
  orgId,
  unitId,
  mode = 'create',
  initialData,
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState<RfidFormData>(emptyFormData);
  const [errors, setErrors] = useState<Partial<RfidFormData>>({});
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  useEffect(() => {
    if (!open) return;

    setFormData(initialData || emptyFormData);
    setErrors({});
  }, [open, initialData]);

  // Fetch companies when modal opens
  useEffect(() => {
    if (open && orgId) {
      const fetchCompanies = async () => {
        try {
          setLoadingCompanies(true);
          const response = await companiesService.getCompanies(orgId);
          if (response.success && response.data) {
            setCompanies(response.data.companies || []);
          }
        } catch (err) {
          console.error('Failed to fetch companies:', err);
        } finally {
          setLoadingCompanies(false);
        }
      };
      fetchCompanies();
    }
  }, [open, orgId]);

  const handleInputChange = (field: keyof RfidFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleCompanyChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      company: value
    }));
    
    // Clear error when user selects a company
    if (errors.company) {
      setErrors(prev => ({
        ...prev,
        company: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<RfidFormData> = {};
    
    if (!formData.rfid.trim()) {
      newErrors.rfid = 'RFID is required';
    }
    if (!formData.company.trim()) {
      newErrors.company = 'Company is required';
    }
    if (!formData.user_name.trim()) {
      newErrors.user_name = 'Name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const payload = {
        ...formData,
        serial_number: formData.serial_number || 'NA',
        company: formData.company,
      };
      onSubmit(payload);
    }
  };

  const handleClose = () => {
    setFormData(emptyFormData);
    setErrors({});
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 3, 
        pb: 2,
        backgroundColor: '#FAFAFA',
        borderBottom: '1px solid #E0E0E0'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontFamily: 'Montserrat, sans-serif'
          }}>
            {mode === 'edit' ? 'Edit RFID Card' : 'Add RFID Card'}
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{ color: theme.palette.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="RFID"
              value={formData.rfid}
              onChange={handleInputChange('rfid')}
              error={!!errors.rfid}
              helperText={errors.rfid}
              placeholder="Enter RFID number"
              disabled={mode === 'edit'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl 
              fullWidth 
              error={!!errors.company}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            >
              <InputLabel>Company</InputLabel>
              <Select
                value={formData.company}
                onChange={handleCompanyChange}
                label="Company"
                disabled={loadingCompanies}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.company_name}>
                    {company.company_name}
                  </MenuItem>
                ))}
              </Select>
              {errors.company && <FormHelperText>{errors.company}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Name"
              value={formData.user_name}
              onChange={handleInputChange('user_name')}
              error={!!errors.user_name}
              helperText={errors.user_name}
              placeholder="Enter user name"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              placeholder="Enter email address"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={handleClose}
          disabled={isLoading}
          sx={{ 
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          sx={{
            backgroundColor: '#337A5C',
            '&:hover': {
              backgroundColor: '#2a6350',
            },
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
          }}
        >
          {isLoading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
              {mode === 'edit' ? 'Saving...' : 'Adding...'}
            </>
          ) : (
            mode === 'edit' ? 'Save Changes' : 'Add RFID'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRfidCardModal;
